import { contrastRatio, mixOklab, shiftLightness } from './color';
import type { LatLon } from './geo';
import { sunAt } from './sun';

/**
 * Sky colors for the "window seat" concept. The sky is the app's one bold element: the Now
 * card and flight screens show the real sky at that place and moment.
 */

export type SkyName =
  | 'night'
  | 'dawn'
  | 'sunrise'
  | 'morning'
  | 'day'
  | 'golden'
  | 'sunset'
  | 'dusk'
  | 'twilight';

export interface Sky {
  /** Gradient stops from top to bottom. */
  stops: [string, string, string];
  /** Text color that stays readable on every stop. */
  ink: string;
  /** 'light' when ink is white, 'dark' when ink is near-black. */
  inkTone: 'light' | 'dark';
  /** 0..1, how visible the stars are. */
  stars: number;
  /** Nearest named keyframe, for tests and debugging. */
  name: SkyName;
  /** Sun altitude in degrees. */
  altitude: number;
}

interface Keyframe {
  alt: number;
  name: SkyName;
  stops: [string, string, string];
  stars: number;
}

const NIGHT: Keyframe = {
  alt: -18,
  name: 'night',
  stops: ['#050A1A', '#0B1430', '#16244A'],
  stars: 1,
};
const DAY: Keyframe = { alt: 15, name: 'day', stops: ['#4F8FDB', '#8DBBEB', '#CFE4F7'], stars: 0 };

/** Sun rising, ordered by altitude. */
const MORNING: Keyframe[] = [
  NIGHT,
  { alt: -12, name: 'twilight', stops: ['#0A1230', '#23285A', '#4E3B66'], stars: 0.75 },
  { alt: -6, name: 'dawn', stops: ['#0E1838', '#3B3F74', '#94627E'], stars: 0.35 },
  { alt: -1, name: 'sunrise', stops: ['#6F8BC4', '#D9A6AB', '#F5C89E'], stars: 0 },
  { alt: 6, name: 'morning', stops: ['#5E95DA', '#A9C8EC', '#E8E6E4'], stars: 0 },
  DAY,
];

/** Sun setting, ordered by altitude. */
const EVENING: Keyframe[] = [
  NIGHT,
  { alt: -12, name: 'twilight', stops: ['#0A1230', '#23285A', '#4E3B66'], stars: 0.75 },
  { alt: -5, name: 'dusk', stops: ['#141C45', '#4A3B78', '#A85A5C'], stars: 0.3 },
  { alt: 0, name: 'sunset', stops: ['#5A6FA8', '#D9958C', '#F3BE8E'], stars: 0 },
  { alt: 6, name: 'golden', stops: ['#6D95CF', '#E9B38C', '#F6D3A0'], stars: 0 },
  DAY,
];

export const SKY_KEYFRAMES = { morning: MORNING, evening: EVENING };

const WHITE = '#FFFFFF';
const DARK_INK = '#0E1A2E';
const MIN_CONTRAST = 4.5;

function minContrast(ink: string, stops: string[]): number {
  return Math.min(...stops.map((s) => contrastRatio(ink, s)));
}

/** Nudges stops darker (white ink) or lighter (dark ink) until every stop passes. */
export function ensureReadable(
  stops: [string, string, string],
  ink: string,
): [string, string, string] {
  const dir = ink === WHITE ? -1 : 1;
  return stops.map((s) => {
    let c = s;
    for (let i = 0; i < 40 && contrastRatio(ink, c) < MIN_CONTRAST; i++)
      c = shiftLightness(c, dir * 0.015);
    return c;
  }) as [string, string, string];
}

/** Sky for a given sun altitude. `morning` picks the rising or setting palette. */
export function skyForAltitude(altitude: number, morning: boolean): Sky {
  const frames = morning ? MORNING : EVENING;
  let lo = frames[0]!;
  let hi = frames[frames.length - 1]!;
  let t = 0;
  if (altitude <= lo.alt) {
    hi = lo;
  } else if (altitude >= hi.alt) {
    lo = hi;
  } else {
    for (let i = 1; i < frames.length; i++) {
      const f = frames[i]!;
      if (altitude <= f.alt) {
        lo = frames[i - 1]!;
        hi = f;
        t = (altitude - lo.alt) / (hi.alt - lo.alt);
        break;
      }
    }
  }
  const mixed = lo.stops.map((s, i) => mixOklab(s, hi.stops[i]!, t)) as [string, string, string];
  const white = minContrast(WHITE, mixed);
  const dark = minContrast(DARK_INK, mixed);
  const ink = white >= dark ? WHITE : DARK_INK;
  const stops = ensureReadable(mixed, ink);
  return {
    stops,
    ink,
    inkTone: ink === WHITE ? 'light' : 'dark',
    stars: lo.stars + (hi.stars - lo.stars) * t,
    name: t < 0.5 ? lo.name : hi.name,
    altitude,
  };
}

/** The real sky at a place and moment. */
export function skyAt(place: LatLon, ms: number): Sky {
  const sun = sunAt(place, ms);
  return skyForAltitude(sun.altitude, sun.morning);
}

/** CSS background for a sky. */
export function skyGradientCss(sky: Sky): string {
  const [a, b, c] = sky.stops;
  return `linear-gradient(180deg, ${a} 0%, ${b} 55%, ${c} 100%)`;
}
